import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-time-picker/paper-time-picker.js';
import '@material/web/dialog/dialog.js';
import '@cbar/paper-date-picker/paper-date-picker.js';
import '@cbar/paper-date-picker/paper-date-picker-dialog-style.js';
import '@polymer/paper-dialog/paper-dialog.js';
import '@polymer/paper-button/paper-button.js';

/**
 * `communitybuildar-people-date-time-picker`
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarDateTimePicker extends PolymerElement {
  static get template() {
    return html`
        <style include="paper-date-picker-dialog-style"></style>
        <style include="paper-time-picker-dialog-style"></style>

        <style>
            #labelButtons {
                @apply --layout-horizontal;
                @apply --layout-center;

                @apply --cbar-date-time-picker-label-buttons;
            }

            [invalid] {
                color: red;
            }
            .paper-date-picker-dialog, .paper-time-picker-dialog{
                margin: auto !important;
            }
        </style>

        <template is="dom-if" if="[[showLabelButtons]]">
            <div id="labelButtons">
                <slot name="label"></slot>
                <paper-button on-tap="openDateDialog" invalid$="[[invalid]]" disabled$="[[disabled]]">[[_date]]</paper-button>

                <paper-button on-tap="openTimeDialog" invalid$="[[invalid]]" disabled$="[[disabled]]">[[_time]]</paper-button>
            </div>
        </template>

        <md-dialog id="dateDialog" class="paper-date-picker-dialog picker" open="{{dateOpened}}">
            <paper-date-picker class="picker" date="{{_datePickerDate}}" min-date="[[_datePickerMinDate]]" max-date="3000-12-12" force-narrow slot="content"></paper-date-picker>
            <div class="buttons" slot="actions">
                <paper-button on-tap="__closeHandler" class="cancel">Cancel</paper-button>
                <paper-button on-tap="__closeHandler" class="confirm">OK</paper-button>
            </div>
        </md-dialog>

        <md-dialog id="timeDialog" class="paper-time-picker-dialog" open="{{timeOpened}}">
            <paper-time-picker time="{{_datePickerTime}}" force-narrow slot="content" class="picker"></paper-time-picker>
            <div class="buttons" slot="actions">
                <paper-button on-tap="__closeHandler" class="cancel">Cancel</paper-button>
                <paper-button on-tap="__closeHandler" class="confirm">OK</paper-button>
            </div>
        </md-dialog>
    `;
  }

  static get is() {
    return 'cbar-date-time-picker';
  }

  static get properties() {
    return {
      /**
       * The Date object that will be used to populate the
       * date and time pickers (or a date string in YYYY-MM-DD HH:mm format)
       */
      date: {
        type: Date,
        observer: '_dateChanged',
        notify: true,
      },

      /**
       * The formatted date string based on the 'format' property
       */
      formatted: {
        type: String,
        readOnly: true,
        notify: true,
      },

      /**
       * The format to use for the 'formatted' property
       * (refer to Moment js documentation for formats)
       */
      format: {
        type: String,
        value: 'DD-MM-YYYY HH:mm',
      },

      /**
       * The minimum date that the date picker is allowed
       * to be set to
       */
      minDate: {
        type: Date,
        observer: '_minDateChanged',
      },

      /**
       * Whether this date picker is valid (i.e. the date is
       * greater than the minDate)
       */
      invalid: {
        type: Boolean,
        value: false,
        notify: true,
      },

      /**
       * Whether to show the date/time label buttons that displat
       * the date/time and can be tapped to access the date/time
       * picker dialogs
       */
      showLabelButtons: {
        type: Boolean,
        value: false,
      },

      /**
       * Whether to automatically open the time dialog on the confirmation
       * of the date dialog
       */
      autoOpenTimeDialog: {
        type: Boolean,
        value: false,
      },

      /**
       * Whether the date dialog is opened or not
       */
      dateOpened: {
        type: Boolean,
        notify: true,
        value: false,
      },

      /**
       * Whether the time dialog is opened or not
       */
      timeOpened: {
        type: Boolean,
        notify: true,
        value: false,
      },
      withBackdrop: {
        type: Boolean,
        value: false
      },
      _date: {
        type: String,
        value: "Set Date"
      },
      _time: {
        type: String,
        value: "Set Time"
      },
      disabled: {
        type: Boolean,
        value: false
      }
    };
  }

  openDateDialog() {
    this._showDateTimeDialog(this.$.dateDialog);
  }

  openTimeDialog() {
    this._showDateTimeDialog(this.$.timeDialog);
  }

  /**
   * Validates that the date is not less than the specified
   * minDate
   *
   * @returns {boolean}
   */
  validate() {
    this.invalid = !!this.date && this.date < this._minDate;

    return !this.invalid;
  }

  /**
   * Abstract function for showing a date or time dialog
   *
   * @param {HTMLElement} dialog The dialog to show.
   * @private
   */
  _showDateTimeDialog(dialog) {
    dialog.show();

    let closedHandler = (e) => {
      // Stop listening once handled.
      dialog.removeEventListener('iron-overlay-closed', closedHandler);

      if( this.confirmed ) {
        let shouldOpenTimeDialog = this.autoOpenTimeDialog && dialog === this.$.dateDialog;
        if( shouldOpenTimeDialog ) {
          return this.openTimeDialog();
        }

        // At the point that the dialog is confirmed, the this._date
        // or this._time variables will have been set by the date/time picker
        // via 2-way binding
        this.date = this._getCombinedDate();
      }
    };

    // Listen for when the dialog is closed and update the date if it
    // was confirmed.
    dialog.addEventListener('close', closedHandler);
    // new dialog tpe requires the resize post the animation
    requestAnimationFrame(() => {
      dialog.querySelector('.picker')._resizeHandler();
    });
  }

  /**
   * Combines the Date picker and Time picker values to
   * generate a Date object.
   *
   * @private
   */
  _getCombinedDate() {
    // Get the formatted d/m/y string from our datePickerValue, which is a date
    // object.
    let dmy = this._formatDate(this._datePickerDate, 'DD/MM/YYYY');

    // Combine the d/m/y string of our _date Date with our hours and
    // minutes from the time variable.
    return this._dateFromFormat(`${dmy} ${this._datePickerTime}`, 'DD/MM/YYYY HH:mm A');
  }

  _dateChanged(date) {
    let validDate = true;
    if( !date ) {
      date = {};
      validDate = false;
    }

    if( date && typeof date === 'string' ) {
      // Convert to a date object (observer will be fired again)
      return this.date = new Date(date);
    }

    let momentDate = moment(date);

    // Copy the date into the _datePickerDate
    // variable to ensure we don't update until confirmed.
    this._datePickerDate = momentDate.toDate();
    this._datePickerTime = momentDate.format('hh:mm A');

    // The displayed d/m/Y date.
    this._date = validDate ? momentDate.format('DD/MM/YYYY') : "Set Date";

    // The displayed time.
    this._time = validDate ? momentDate.format('hh:mm A') : "Set Time";

    // The formatted date that can be used by the host for
    // retrieving the required value without extra JS, via 2-way
    // binding.
    this._setFormatted(validDate ? momentDate.format(this.format) : "");
  }

  /**
   * Validates the minimum date and ensures our current
   * date is not less than the supplied minimum date
   *
   * @param {Date|String} minDate
   * @private
   */
  _minDateChanged(minDate) {
    if( !minDate ) {
      return this._minDate = this._datePickerMinDate = null;
    }

    let momentDate = moment(minDate);

    if( !momentDate.isValid() ) {
      throw new Error('Invalid minimum date specified for cbar-date-time-picker');
    }

    minDate = momentDate.toDate();

    if(this.date && this.date < minDate ) {
      // Ensure our date is not initially less than our
      // minimum date
      this.date = minDate;
    }

    this._minDate = momentDate.toDate();
    this._datePickerMinDate = momentDate.startOf('day').toDate();
  }

  _dateFromFormat(date, format) {
    return date ? moment(date, format).toDate() : undefined;
  }

  _formatDate(date, format, fromFormat) {
    return date ? moment(date, fromFormat).format(format) : undefined;
  }

  __closeHandler(e){
    this.confirmed = e.target.classList.contains('confirm');
    e.target.closest('md-dialog').close();
    this.dateOpened = this.timeOpened = false;
  }
}

window.customElements.define(CbarDateTimePicker.is, CbarDateTimePicker);
